import { useEffect, useMemo, useState } from 'react';
import { adminApi, type BandMember, type CalendarEvent, type CalendarKind, type Me } from '../../lib/api';
import { Field, Modal } from './LiveAdminPanel';

const KIND_LABEL: Record<CalendarKind, string> = {
  concert: 'Concerto',
  rehearsal: 'Prova',
  unavailable: 'Indisponibile',
  other: 'Altro impegno',
};

const KIND_COLOR: Record<CalendarKind, string> = {
  concert: '#f59e0b',
  rehearsal: '#3b82f6',
  unavailable: '#ef4444',
  other: '#a855f7',
};

type View = 'month' | 'season';

type EditingDraft = {
  id?: number;
  scope: 'self' | 'all';
  kind: CalendarKind;
  title: string;
  notes: string;
  dateISO: string; // formato YYYY-MM-DD (giorno)
  endDateISO: string; // YYYY-MM-DD per range, '' se nessuno
  location: string;
};

export default function CalendarAdminPanel({ me }: { me: Me }) {
  const today = new Date();
  const [view, setView] = useState<View>('month');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-11 (mese di partenza)
  const [members, setMembers] = useState<BandMember[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditingDraft | null>(null);

  const monthsToShow = view === 'season' ? 3 : 1;
  const rangeStart = useMemo(() => new Date(year, month, 1), [year, month]);
  const rangeEnd = useMemo(() => new Date(year, month + monthsToShow, 0, 23, 59, 59), [year, month, monthsToShow]);

  // Costruisce l'array di colonne (una per giorno) con metadata mese
  const columns = useMemo(() => {
    const cols: { y: number; m: number; d: number; isFirstOfMonth: boolean }[] = [];
    for (let i = 0; i < monthsToShow; i++) {
      const cur = new Date(year, month + i, 1);
      const days = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
      for (let d = 1; d <= days; d++) {
        cols.push({ y: cur.getFullYear(), m: cur.getMonth(), d, isFirstOfMonth: d === 1 });
      }
    }
    return cols;
  }, [year, month, monthsToShow]);

  async function reload() {
    setLoading(true);
    const [m, e] = await Promise.all([
      adminApi.memberList(),
      adminApi.calendarList({ start: rangeStart.toISOString(), end: rangeEnd.toISOString() }),
    ]);
    setMembers(m);
    setEvents(e);
    setLoading(false);
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [year, month, view]);

  function shift(delta: number) {
    const step = view === 'season' ? 3 : 1;
    const d = new Date(year, month + delta * step, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  }

  function newDraft(memberId: number | null, ymd?: { y: number; m: number; d: number }): EditingDraft {
    const day = ymd || { y: today.getFullYear(), m: today.getMonth(), d: today.getDate() };
    const dateISO = toISODate(day.y, day.m, day.d);
    // Se l'utente clicca su una riga di un altro membro, comunque il default è 'self' (può solo creare per sé)
    return {
      scope: 'self',
      kind: 'rehearsal',
      title: '',
      notes: '',
      dateISO,
      endDateISO: '',
      location: '',
    };
  }

  function openNew(memberId: number | null, ymd?: { y: number; m: number; d: number }) {
    setEditing(newDraft(memberId, ymd));
  }

  function openEdit(ev: CalendarEvent) {
    const start = new Date(ev.start);
    const end = ev.end ? new Date(ev.end) : null;
    const isAll = ev.member_ids.length === 0;
    setEditing({
      id: ev.id,
      scope: isAll ? 'all' : 'self',
      kind: ev.kind,
      title: ev.title,
      notes: ev.notes || '',
      dateISO: toISODate(start.getFullYear(), start.getMonth(), start.getDate()),
      endDateISO: end ? toISODate(end.getFullYear(), end.getMonth(), end.getDate()) : '',
      location: ev.location || '',
    });
  }

  async function save() {
    if (!editing) return;
    if (!editing.dateISO) { alert('Scegli una data.'); return; }

    // scope=all consentito solo per concerti
    const scope = editing.kind === 'concert' ? editing.scope : 'self';
    const kind = scope === 'all' ? 'concert' : editing.kind;

    // member_ids: vuoto = tutti, altrimenti solo me
    let member_ids: number[] = [];
    if (scope === 'self') {
      if (!me.member_id) { alert('Il tuo utente non è collegato a un membro della band. Contatta l\'admin.'); return; }
      member_ids = [me.member_id];
    }

    const start = isoFromDate(editing.dateISO);
    const end = editing.endDateISO ? isoFromDate(editing.endDateISO, true) : null;

    const data: any = {
      kind,
      title: editing.title || KIND_LABEL[kind],
      notes: editing.notes || '',
      start,
      end,
      all_day: true,
      location: editing.location || '',
      member_ids,
    };
    if (editing.id) await adminApi.calendarUpdate(editing.id, data);
    else await adminApi.calendarCreate(data);
    setEditing(null);
    reload();
  }

  async function remove() {
    if (!editing?.id) return;
    if (!confirm('Eliminare questo evento?')) return;
    await adminApi.calendarDelete(editing.id);
    setEditing(null);
    reload();
  }

  // Eventi che riguardano questo membro su un giorno (compresi range, e gli eventi "tutti")
  function eventsFor(memberId: number, col: { y: number; m: number; d: number }): CalendarEvent[] {
    const dayDate = new Date(col.y, col.m, col.d);
    return events.filter((ev) => {
      const involves = ev.member_ids.length === 0 || ev.member_ids.includes(memberId);
      if (!involves) return false;
      const s = new Date(ev.start);
      const sDay = new Date(s.getFullYear(), s.getMonth(), s.getDate());
      if (!ev.end) return sDay.getTime() === dayDate.getTime();
      const e = new Date(ev.end);
      const eDay = new Date(e.getFullYear(), e.getMonth(), e.getDate());
      return dayDate >= sDay && dayDate <= eDay;
    });
  }

  const headerLabel = useMemo(() => {
    if (view === 'season') {
      const last = new Date(year, month + 2, 1);
      const a = rangeStart.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      const b = last.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      return `${a} → ${b}`;
    }
    return rangeStart.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  }, [view, year, month, rangeStart]);

  const cellWidth = view === 'season' ? 26 : 36;

  return (
    <section className="admin-panel">
      <div className="admin-panel-head">
        <div className="cal-nav">
          <button className="btn-ghost" onClick={() => shift(-1)}>‹</button>
          <h2 style={{ textTransform: 'capitalize', margin: 0 }}>{headerLabel}</h2>
          <button className="btn-ghost" onClick={() => shift(1)}>›</button>
          <button className="btn-ghost" onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }}>Oggi</button>
          <div className="cal-view-switch">
            <button className={view === 'month' ? 'is-active' : ''} onClick={() => setView('month')}>Mese</button>
            <button className={view === 'season' ? 'is-active' : ''} onClick={() => setView('season')}>3 mesi</button>
          </div>
        </div>
        <button className="btn-primary" onClick={() => openNew(null)}>+ Nuovo evento</button>
      </div>

      <div className="cal-legend">
        {(Object.keys(KIND_LABEL) as CalendarKind[]).map((k) => (
          <span key={k} className="cal-legend-item"><span className="cal-dot" style={{ background: KIND_COLOR[k] }} />{KIND_LABEL[k]}</span>
        ))}
      </div>

      {loading && <p className="muted">Caricamento...</p>}

      <div className="cal-scroll">
        <div className="cal-grid" style={{ gridTemplateColumns: `180px repeat(${columns.length}, ${cellWidth}px)` }}>
          <div className="cal-corner">Membro \\ Giorno</div>
          {columns.map((c, i) => {
            const date = new Date(c.y, c.m, c.d);
            const dow = date.toLocaleDateString('it-IT', { weekday: 'short' });
            const isToday = date.toDateString() === today.toDateString();
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const monthSep = c.isFirstOfMonth && i > 0;
            return (
              <div
                key={`h-${c.y}-${c.m}-${c.d}`}
                className={`cal-head ${isToday ? 'is-today' : ''} ${isWeekend ? 'is-weekend' : ''} ${monthSep ? 'cal-month-sep' : ''}`}
                title={date.toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long' })}
              >
                <div className="cal-head-d">{c.d}</div>
                {view === 'month' && <div className="cal-head-w">{dow}</div>}
                {view === 'season' && c.isFirstOfMonth && (
                  <div className="cal-head-month">{date.toLocaleDateString('it-IT', { month: 'short' })}</div>
                )}
              </div>
            );
          })}

          {members.map((m) => (
            <Row key={m.id}>
              <div className="cal-member">
                <span className="cal-color" style={{ background: m.color }} />
                <div>
                  <div>{m.display_name}</div>
                  {m.instrument && <div className="muted" style={{ fontSize: 11 }}>{m.instrument}</div>}
                </div>
              </div>
              {columns.map((c, i) => {
                const evs = eventsFor(m.id, c);
                const monthSep = c.isFirstOfMonth && i > 0;
                return (
                  <div
                    key={`${m.id}-${c.y}-${c.m}-${c.d}`}
                    className={`cal-cell ${monthSep ? 'cal-month-sep' : ''}`}
                    onClick={() => openNew(m.id, c)}
                  >
                    {evs.map((ev) => (
                      <button
                        key={ev.id}
                        className="cal-pill"
                        style={{ background: KIND_COLOR[ev.kind] }}
                        title={`${KIND_LABEL[ev.kind]}: ${ev.title}`}
                        onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                      />
                    ))}
                  </div>
                );
              })}
            </Row>
          ))}
        </div>
      </div>

      {editing && (
        <Modal title={editing.id ? 'Modifica evento' : 'Nuovo evento'} onClose={() => setEditing(null)}>
          <div className="scope-toggle">
            <button
              type="button"
              className={editing.scope === 'self' ? 'is-on' : ''}
              onClick={() => setEditing({ ...editing, scope: 'self' })}
            >👤 Solo per me</button>
            <button
              type="button"
              className={editing.scope === 'all' ? 'is-on' : ''}
              onClick={() => setEditing({ ...editing, scope: 'all', kind: 'concert' })}
            >🎤 Concerto per tutti</button>
          </div>

          {editing.scope === 'self' && (
            <Field label="Tipo">
              <select
                value={editing.kind}
                onChange={(e) => setEditing({ ...editing, kind: e.target.value as CalendarKind })}
              >
                {(Object.keys(KIND_LABEL) as CalendarKind[]).filter((k) => k !== 'concert').map((k) => (
                  <option key={k} value={k}>{KIND_LABEL[k]}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label={editing.scope === 'all' ? 'Nome / titolo concerto' : 'Titolo (opzionale)'}>
            <input
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              placeholder={editing.scope === 'all' ? 'Es. Live al Binario 9' : ''}
            />
          </Field>

          <Field label="Giorno">
            <input
              type="date"
              value={editing.dateISO}
              onChange={(e) => setEditing({ ...editing, dateISO: e.target.value })}
            />
          </Field>
          <Field label="Fine (opzionale, per più giorni)">
            <input
              type="date"
              value={editing.endDateISO}
              min={editing.dateISO}
              onChange={(e) => setEditing({ ...editing, endDateISO: e.target.value })}
            />
          </Field>

          {editing.scope === 'all' && (
            <Field label="Locale, Città">
              <input
                value={editing.location}
                onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                placeholder="Es. Binario 9, Trieste"
              />
            </Field>
          )}

          <Field label="Note">
            <textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={2} />
          </Field>

          {editing.scope === 'all' && (
            <p className="muted" style={{ marginTop: -8 }}>
              Verrà creata anche la data nella sezione Live del sito pubblico.
            </p>
          )}

          <div className="modal-actions">
            {editing.id && <button className="btn-danger" onClick={remove}>Elimina</button>}
            <button className="btn-ghost" onClick={() => setEditing(null)}>Annulla</button>
            <button className="btn-primary" onClick={save}>Salva</button>
          </div>
        </Modal>
      )}
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function toISODate(y: number, m: number, d: number) {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${y}-${pad(m + 1)}-${pad(d)}`;
}

function isoFromDate(ymd: string, endOfDay = false) {
  // ymd = 'YYYY-MM-DD' interpretato come data locale
  const [y, m, d] = ymd.split('-').map(Number);
  const date = endOfDay ? new Date(y, m - 1, d, 23, 59, 0) : new Date(y, m - 1, d, 12, 0, 0);
  return date.toISOString();
}
